/**
 * GoalStep.js  — Assessment Wizard, Screen 3 "Goal"
 *
 * Collects the user's primary objective, used to tailor calorie and
 * nutrition targets downstream. Built on the shared AssessmentStepShell so
 * the progress bar, step counter, question header, and Previous/Next
 * footer stay identical to every other wizard screen.
 *
 * Selected-state uses a 2.4px emerald border only (no fill), per spec —
 * consistent with the Screen 1 emerald identity rather than the saffron
 * accent used elsewhere in the app.
 *
 * Rule source: FSD v1.0 Screen 3 (Goal) Functional Rules + FSD v2.1
 * §3.4 Pregnancy engine. Two v1.0 requirements were previously
 * unimplemented and are fixed here:
 *  - The spec calls for 9 selectable goal cards; this component only had
 *    8 — "Postpartum Recovery" was missing entirely and is restored.
 *  - "If Gender (Screen 2) = Male, hide 'Pregnancy Nutrition' and
 *    'Postpartum Recovery' cards entirely" was never implemented (this
 *    component didn't even receive a `gender` prop). Fixed via the new
 *    `gender` prop.
 *
 * FSD v2.1 §3.4 change: the single "Pregnancy Nutrition" card is now two
 * cards — "Healthy Pregnancy Nutrition" and "Medical Restriction
 * Support" — and, when the caller already knows the user is pregnant
 * (`isPregnant` prop, known only after Screen 11 on a later visit to this
 * step), "Lose Weight" and "Muscle Gain" must not be user-selectable
 * goals for a pregnant user. Note: pregnancy status usually isn't known
 * yet on a user's first pass through this screen (Screen 11 comes later
 * in the flow) — `isPregnant` only applies when the wizard re-renders
 * this step after Screen 11 has already been completed.
 *
 * Navigation contract (unchanged): `onNext` is called with the selected
 * goal id and is expected to advance the wizard to Screen 4 (Lifestyle) —
 * that's still the wizard's responsibility, not this component's.
 *
 * Standalone fallback: if no `onNext` prop is passed at all (e.g. this
 * component is being rendered on its own, before a wizard exists yet),
 * GoalStep renders LifestyleStep itself so Next → Lifestyle works out of
 * the box. The moment a real `onNext` is supplied, this fallback never
 * fires and the wizard is fully in control, same as before.
 *
 * Privacy (Nutrition Policy v1.0 §19): raw biometric values (weightKg,
 * targetWeightKg) are used locally to decide whether to show the target-
 * weight warning, but are never forwarded to analytics — only a boolean
 * flag is sent. See useGoalAnalytics.js.
 */

import React, { useState, useCallback, useEffect } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import LifestyleStep from "../Lifestyle/LifestyleStep";
import useGoalAnalytics from "./useGoalAnalytics";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  CardGrid,
  GoalCard,
  GoalCardLabel,
  InfoNote,
  WarningBox,
  WarningText,
  WarningActions,
  WarningButton,
  WarningButtonPrimary,
} from "./GoalStep.styles";

// ─── Goal options ────────────────────────────────────────────────────────────
// id is the stable value persisted to the assessment record / sent to
// analytics; label is the display text (matches the screenshot exactly).
//
// FSD v2.1 §3.4: the old single "Pregnancy Nutrition" card is replaced by
// two cards — pregnancy_nutrition ("Healthy Pregnancy Nutrition") and
// pregnancy_medical_restriction ("Medical Restriction Support") — pushing
// the total from the FSD v1.0 baseline of 9 cards to 10.
const GOAL_OPTIONS = [
  { id: "lose_weight",                     label: "Lose\nWeight" },
  { id: "gain_weight",                     label: "Gain\nWeight" },
  { id: "maintain_weight",                 label: "Maintain\nWeight" },
  { id: "muscle_gain",                     label: "Muscle\nGain" },
  { id: "improve_fitness",                 label: "Improve\nFitness" },
  { id: "better_digestion",                label: "Better\nDigestion" },
  { id: "healthy_lifestyle",               label: "Healthy\nLifestyle" },
  { id: "pregnancy_nutrition",             label: "Healthy Pregnancy\nNutrition", pregnancyRelated: true },
  { id: "pregnancy_medical_restriction",   label: "Medical Restriction\nSupport", pregnancyRelated: true },
  { id: "postpartum_recovery",             label: "Postpartum\nRecovery", pregnancyRelated: true },
];

// Goal ids that FSD v2.1 §3.4 disallows selecting once the user is known
// to be pregnant (pregnancy uses a dedicated energy pathway, not an
// ordinary deficit/surplus engine).
const GOAL_IDS_BLOCKED_WHEN_PREGNANT = ["lose_weight", "muscle_gain"];

const STEP_NUMBER = STEP_NUMBERS.GOAL;

/**
 * @param {object}   props
 * @param {string}   props.sessionId       - Anonymous session id from Screen 1.
 * @param {string}   [props.initialGoal]   - Previously selected goal id, if the
 *   user navigated back to this step.
 * @param {"male"|"female"|"other"} [props.gender] - Gender captured on Screen 2.
 *   Male hides the pregnancy/postpartum cards entirely (FSD v1.0 Screen 3
 *   Functional Rules — previously unimplemented).
 * @param {boolean}  [props.isPregnant]      - Only meaningful once known
 *   (i.e. after Screen 11 has been completed on an earlier pass through
 *   the wizard). When true, "Lose Weight" and "Muscle Gain" are disabled
 *   per FSD v2.1 §3.4 — pregnancy uses a dedicated energy pathway.
 * @param {number}   [props.weightKg]        - Current weight (kg) captured on
 *   Screen 2, used for the "Lose Weight" target-weight sanity check.
 * @param {number}   [props.targetWeightKg]  - Optional target weight (kg)
 *   captured on Screen 2.
 * @param {function} props.onPrevious      - Go to Screen 2.
 * @param {function} [props.onNext]        - Go to Screen 4, receives the selected
 *   goal id. If omitted, GoalStep falls back to rendering LifestyleStep itself
 *   (see file header) — pass this once a wizard owns step routing.
 */
const GoalStep = ({
  sessionId,
  initialGoal = null,
  gender = null,
  isPregnant = false,
  weightKg = null,
  targetWeightKg = null,
  onPrevious,
  onNext,
}) => {
  const analytics = useGoalAnalytics();
  const [selectedGoal, setSelectedGoal] = useState(initialGoal);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);
  // Only used by the standalone fallback described above — irrelevant once
  // a real `onNext` is passed in by a wizard.
  const [showLifestyleFallback, setShowLifestyleFallback] = useState(false);

  // FSD v1.0 Screen 3 Functional Rules: hide pregnancy/postpartum cards
  // entirely when gender is Male.
  const visibleGoalOptions = React.useMemo(
    () => GOAL_OPTIONS.filter((opt) => !(gender === "male" && opt.pregnancyRelated)),
    [gender]
  );

  // "If provided and goal is later 'Weight Loss' but target > current
  // weight, show a soft warning asking the user to confirm."
  const needsTargetWeightWarning =
    selectedGoal === "lose_weight" &&
    typeof weightKg === "number" &&
    typeof targetWeightKg === "number" &&
    targetWeightKg > weightKg;

  const showWarning = needsTargetWeightWarning && !warningAcknowledged;

  const handleSelect = useCallback(
    (option) => {
      if (isPregnant && GOAL_IDS_BLOCKED_WHEN_PREGNANT.includes(option.id)) return;
      setSelectedGoal(option.id);
      setWarningAcknowledged(false); // re-check the warning for the newly selected goal
      analytics.trackGoalSelect(sessionId, option.id, option.label.replace("\n", " "));
    },
    [analytics, sessionId, isPregnant]
  );

  // If the caller learns isPregnant=true after a goal that's now
  // disallowed was already selected (e.g. user picked "Lose Weight" on an
  // earlier pass, then Screen 11 revealed pregnancy on a later one),
  // clear the now-invalid selection rather than silently keeping it.
  useEffect(() => {
    if (isPregnant && GOAL_IDS_BLOCKED_WHEN_PREGNANT.includes(selectedGoal)) {
      setSelectedGoal(null);
    }
  }, [isPregnant, selectedGoal]);

  const handlePrevious = useCallback(() => {
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  // Single exit point for "goal confirmed, advance" — used by both the
  // normal Next click and the warning's "Continue Anyway" button.
  const advanceToLifestyle = useCallback(
    (goalId) => {
      analytics.trackStepNext(sessionId, goalId);
      if (onNext) {
        onNext(goalId);
      } else {
        // No wizard wired up yet — show Lifestyle directly.
        setShowLifestyleFallback(true);
      }
    },
    [analytics, sessionId, onNext]
  );

  const handleNext = useCallback(() => {
    if (!selectedGoal) return;

    if (needsTargetWeightWarning && !warningAcknowledged) {
      // Don't advance yet — surface the confirmation prompt instead.
      // Analytics gets a boolean only — never the raw weightKg/
      // targetWeightKg values (Nutrition Policy §19 Privacy & Data
      // Minimization).
      analytics.trackTargetWeightWarningShown?.(sessionId, { shown: true });
      return;
    }

    advanceToLifestyle(selectedGoal);
  }, [
    selectedGoal,
    needsTargetWeightWarning,
    warningAcknowledged,
    analytics,
    sessionId,
    weightKg,
    targetWeightKg,
    advanceToLifestyle,
  ]);

  const handleConfirmWarning = useCallback(() => {
    setWarningAcknowledged(true);
    advanceToLifestyle(selectedGoal);
  }, [selectedGoal, advanceToLifestyle]);

  const handleAdjustTarget = useCallback(() => {
    // Send the user back to Screen 2 so they can revise their target weight.
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  // ── Standalone fallback: Lifestyle takes over until it's done ──────────
  if (showLifestyleFallback) {
    return (
      <LifestyleStep
        sessionId={sessionId}
        onPrevious={() => setShowLifestyleFallback(false)}
        onNext={(lifestyleData) => {
          // No Screen 5 (Activity) yet — log for now so the handoff is
          // visible during standalone testing. A wizard's real onNext
          // (passed into GoalStep above) replaces this entire branch.
          // eslint-disable-next-line no-console
          console.log("[GoalStep fallback] lifestyle complete →", lifestyleData);
        }}
      />
    );
  }

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="What's your goal?"
      description="This helps us tailor your calorie and nutrition targets."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!selectedGoal}
      nextLabel={showWarning ? "Review Above" : "Next"}
    >
      <CardGrid role="radiogroup" aria-label="Select your goal">
        {visibleGoalOptions.map((option) => {
          const active = option.id === selectedGoal;
          const disabled = isPregnant && GOAL_IDS_BLOCKED_WHEN_PREGNANT.includes(option.id);
          return (
            <GoalCard
              key={option.id}
              type="button"
              role="radio"
              aria-checked={active}
              aria-disabled={disabled}
              disabled={disabled}
              $active={active}
              $disabled={disabled}
              onClick={() => handleSelect(option)}
            >
              <GoalCardLabel>
                {option.label.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </GoalCardLabel>
            </GoalCard>
          );
        })}
      </CardGrid>

      {isPregnant && (
        <InfoNote>
          Weight-loss and muscle-gain targets aren't offered during
          pregnancy — we'll use a pregnancy-specific nutrition pathway
          instead.
        </InfoNote>
      )}

      {showWarning && (
        <WarningBox role="alert">
          <WarningText>
            Your target weight ({targetWeightKg} kg) is higher than your
            current weight ({weightKg} kg) — that doesn't line up with "Lose
            Weight." Want to continue anyway, or go back and adjust your
            target?
          </WarningText>
          <WarningActions>
            <WarningButtonPrimary type="button" onClick={handleConfirmWarning}>
              Continue Anyway
            </WarningButtonPrimary>
            <WarningButton type="button" onClick={handleAdjustTarget}>
              Adjust Target Weight
            </WarningButton>
          </WarningActions>
        </WarningBox>
      )}
    </AssessmentStepShell>
  );
};

GoalStep.displayName = "GoalStep";
export default React.memo(GoalStep);